// Web shim for React Native NativeModules
// 延迟导入 WebLoadingUI 以避免循环依赖
let showWebLoading: ((msg?: string) => void) | null = null;
let hideWebLoading: (() => void) | null = null;
let showWebToast: ((msg: string) => void) | null = null;

// 延迟加载 WebLoadingUI
const loadWebLoadingUI = () => {
  if (!showWebLoading) {
    try {
      const WebLoadingUI = require('../WebLoadingUI');
      showWebLoading = WebLoadingUI.showWebLoading;
      hideWebLoading = WebLoadingUI.hideWebLoading;
      showWebToast = WebLoadingUI.showWebToast;
    } catch (e) {
      // 如果加载失败，使用默认实现
      showWebLoading = (msg?: string) => console.log('Loading:', msg);
      hideWebLoading = () => console.log('Loading hidden');
      showWebToast = (msg: string) => console.log('Toast:', msg);
    }
  }
};

// 确保 window 可用
const getWindow = () => {
  if (typeof window !== 'undefined') {
    return window;
  }
  return {} as any;
};

// 模拟原生模块，提供空实现或Web兼容实现
const NativeModules: any = {
  // 权限模块
  PermissionModule: {
    isGranted: (permissions: string[], callback: (granted: boolean) => void) => {
      // Web环境下，检查通知权限
      const win = getWindow();
      if ('Notification' in win) {
        callback(win.Notification.permission === 'granted');
      } else {
        callback(false);
      }
    },
    checkPermission: (permission: string, callback: (granted: boolean) => void) => {
      // Web环境下，大多数权限默认授予
      callback(true);
    },
  },
  
  // Toast模块（保留兼容）
  ToastModule: {
    show: (message: string, duration: number) => {
      console.log('Toast:', message);
    },
  },
  
  // Android Toast
  ReactToast: {
    showToast: (message: string) => {
      console.log('Toast (Android):', message);
      // 显示简单的 Web Toast
      loadWebLoadingUI();
      if (showWebToast) {
        showWebToast(message);
      }
    },
  },
  
  // iOS Toast
  ToastIOSModule: {
    show: (options: any) => {
      console.log('Toast (iOS):', options.message);
      loadWebLoadingUI();
      if (showWebToast) {
        showWebToast(options.message);
      }
    },
  },
  
  // 加载模块
  LoadingModule: {
    showLoading: (msg?: string) => {
      console.log('Loading shown:', msg || '');
      loadWebLoadingUI();
      if (showWebLoading) {
        showWebLoading(msg || '加载中...');
      }
    },
    hideLoading: () => {
      console.log('Loading hidden');
      loadWebLoadingUI();
      if (hideWebLoading) {
        hideWebLoading();
      }
    },
  },
  
  // 图片选择器
  ImagePickerModule: {
    openImagePicker: (options: any, callback: any) => {
      console.warn('ImagePicker not available in Web');
      callback({ error: 'Not supported in Web' });
    },
  },
  
  // 音频播放器
  AudioPlayerManager: {
    play: (path: string) => console.log('Playing audio:', path),
    pause: () => console.log('Audio paused'),
    stop: () => console.log('Audio stopped'),
  },
  
  // 音频录制器
  AudioRecorderManager: {
    start: (path: string) => console.log('Recording started:', path),
    stop: () => console.log('Recording stopped'),
  },
  
  // 二维码扫描
  QRCodeScanner: {
    scan: (callback: any) => {
      console.warn('QRCodeScanner not available in Web');
      callback({ error: 'Not supported in Web' });
    },
  },
  
  // 视频缩略图
  VideoThumbnailModule: {
    getThumbnail: (path: string, callback: any) => {
      console.warn('VideoThumbnail not available in Web');
      callback({ error: 'Not supported in Web' });
    },
  },
  
  // 媒体信息
  MediaInfoManager: {
    getMediaInfo: (path: string, callback: any) => {
      callback({ width: 0, height: 0, duration: 0 });
    },
  },
  
  // JPush服务（极光推送 - PC端不需要，提供空实现）
  JPPServiceModule: {
    init: () => {
      console.log('JPush initialized (Web - disabled for PC)');
      return Promise.resolve();
    },
    setAlias: (alias: string) => {
      console.log('JPush alias set (Web - no-op):', alias);
      return Promise.resolve({ code: 0 });
    },
    removeAlias: () => {
      console.log('JPush removeAlias (Web - no-op)');
      return Promise.resolve({ code: 0 });
    },
    getRegistrationID: () => {
      console.log('JPush getRegistrationID (Web - returning dummy ID)');
      return Promise.resolve('web_dummy_registration_id');
    },
  },
  
  // 图片选择器（Android 原生）
  ReactPictureSelect: {
    openPictureSelect: async (options: any) => {
      console.log('Opening picture selector with options:', options);
      return new Promise((resolve, reject) => {
        // 创建文件选择器
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*,video/*';
        input.multiple = options?.maxSelectNum > 1;
        
        input.onchange = async (e: any) => {
          const files = e.target.files;
          if (!files || files.length === 0) {
            resolve(null);
            return;
          }
          
          const results = [];
          for (let i = 0; i < files.length; i++) {
            const file = files[i];
            const reader = new FileReader();
            
            await new Promise((resolveRead) => {
              reader.onload = (event: any) => {
                const dataUrl = event.target.result;
                results.push({
                  path: dataUrl,
                  realPath: dataUrl,
                  fileName: file.name,
                  mimeType: file.type,
                  size: file.size,
                  width: 0,
                  height: 0,
                  duration: 0,
                  bucketId: 0,
                  chooseModel: file.type.startsWith('video') ? 2 : 1,
                });
                resolveRead(null);
              };
              reader.readAsDataURL(file);
            });
          }
          
          resolve(results);
        };
        
        input.oncancel = () => {
          resolve(null);
        };
        
        input.click();
      });
    },
    openCamera: async (options: any) => {
      console.warn('Camera not available in Web environment');
      return Promise.resolve(null);
    },
  },
  
  // 自定义对话框模块（Android 原生）
  CustomDialogModule: {
    showDialog: (
      title: string,
      message: string,
      cancelText: string,
      cancelColor: string,
      confirmText: string,
      confirmColor: string,
      outDismiss: boolean,
      onConfirm: () => void,
      onCancel: () => void
    ) => {
      console.log('CustomDialogModule.showDialog called (Web):', { title, message });
      
      // 使用浏览器原生的 confirm 对话框
      // 如果有取消按钮，使用 confirm；否则使用 alert
      if (cancelText) {
        const win = getWindow();
        const result = win.confirm(`${title}\n\n${message}`);
        if (result) {
          onConfirm?.();
        } else {
          onCancel?.();
        }
      } else {
        const win = getWindow();
        win.alert(`${title}\n\n${message}`);
        onConfirm?.();
      }
    },
    dismissDialog: () => {
      console.log('CustomDialogModule.dismissDialog called (Web - no-op)');
      // Web 环境下的 alert/confirm 是同步的，无需手动关闭
    },
  },
  
  // StatusBarManager (iOS)
  StatusBarManager: {
    HEIGHT: 0, // Web 环境下状态栏高度为 0
    getHeight: (callback: (response: { height: number }) => void) => {
      // Web 平台：直接返回 0
      callback({ height: 0 });
    },
  },
  
  // CommonNativeModule (通用原生模块)
  CommonNativeModule: {
    // 空实现
  },
  
  // GlideModule (Android 图片缓存模块)
  GlideModule: {
    getCacheFile: (url: string): Promise<string> => {
      // Web 环境下，浏览器管理图片缓存，无法直接获取缓存文件路径
      // 返回空字符串或拒绝 Promise
      console.log('GlideModule.getCacheFile called (Web):', url);
      return Promise.resolve('');
    },
  },
  
  // FastImageView (react-native-fast-image 的原生模块)
  FastImageView: {
    preload: (sources: any[]) => {
      console.log('FastImageView.preload called (Web):', sources);
      return Promise.resolve();
    },
    clearMemoryCache: () => {
      console.log('FastImageView.clearMemoryCache called (Web - no-op)');
      return Promise.resolve();
    },
    clearDiskCache: () => {
      console.log('FastImageView.clearDiskCache called (Web - no-op)');
      return Promise.resolve();
    },
  },
  
  // ToastIOSModule (iOS Toast，已在上面定义，这里确保存在)
  // ToastIOSModule 已经在上面定义了
};

export default NativeModules;

